import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Instagram } from "lucide-react";
import { INSTAGRAM_URL } from "../campaigns";
import { useSiteText } from "../lib/content";

const ease = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Projection Advertising",
  "Architectural Mapping",
  "Immersive Experiences",
  "Live Events",
  "Installations",
];

const EMAIL = "contact@luminartprojection.com";
const PHONE = "+44 7346 803435";

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    preferred_date: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const t = useSiteText();

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.company.trim())
      e.company = "Please enter your company, brand or agency.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address.";
    if (!form.service) e.service = "Please select a service.";
    if (!form.message.trim()) e.message = "Please add a message.";
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(
        "Thank you — your enquiry has been sent. We aim to respond within 24 hours."
      );
      setForm({
        name: "",
        company: "",
        email: "",
        phone: "",
        location: "",
        preferred_date: "",
        service: "",
        message: "",
      });
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Your message could not be delivered. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-white/15 rounded-none px-0 py-4 text-white placeholder:text-white/30 focus:outline-none focus:border-white transition-colors duration-300";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-32 md:py-48 px-6 md:px-12 lg:px-24 bg-[#070708] border-t border-white/5"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
        {/* Left */}
        <div>
          <span className="text-xs uppercase tracking-[0.35em] text-[#c8b99d]">
            {t("contact_label", "Contact")}
          </span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease }}
            className="mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-white"
          >
            {t("contact_headline_a", "Let's create something")}{" "}
            <span className="text-[#c8b99d] italic">{t("contact_headline_b", "worth seeing.")}</span>
          </motion.h2>
          <p className="mt-8 max-w-md text-lg text-white/50 font-light leading-relaxed">
            {t(
              "contact_paragraph",
              "Tell us what you're planning. From brand experiences and immersive installations to live events and permanent projects, we'll help bring your vision to life."
            )}
          </p>

          <div className="mt-14 space-y-6 text-white/50">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/30 mb-1">
                Email
              </div>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email-link"
                className="text-lg text-white/80 hover:text-white transition-colors"
              >
                {EMAIL}
              </a>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/30 mb-1">
                Phone
              </div>
              <a
                href={`tel:${PHONE.replace(/\s/g, "")}`}
                data-testid="contact-phone-link"
                className="text-lg text-white/80 hover:text-white transition-colors"
              >
                {PHONE}
              </a>
            </div>
            <div className="pt-2">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors"
              >
                <Instagram size={18} strokeWidth={1.5} /> Instagram
              </a>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <motion.form
          onSubmit={submit}
          noValidate
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, delay: 0.15, ease }}
          className="flex flex-col gap-7"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            <div>
              <input
                data-testid="contact-name-input"
                className={`${inputCls}${errors.name ? " border-red-400/70 focus:border-red-400" : ""}`}
                placeholder="Name *"
                value={form.name}
                onChange={update("name")}
              />
              {errors.name && (
                <p data-testid="error-name" className="mt-2 text-xs text-red-400/90">
                  {errors.name}
                </p>
              )}
            </div>
            <div>
              <input
                data-testid="contact-company-input"
                className={`${inputCls}${errors.company ? " border-red-400/70 focus:border-red-400" : ""}`}
                placeholder="Company / Brand / Agency *"
                value={form.company}
                onChange={update("company")}
              />
              {errors.company && (
                <p data-testid="error-company" className="mt-2 text-xs text-red-400/90">
                  {errors.company}
                </p>
              )}
            </div>
            <div>
              <input
                data-testid="contact-email-input"
                type="email"
                className={`${inputCls}${errors.email ? " border-red-400/70 focus:border-red-400" : ""}`}
                placeholder="Email *"
                value={form.email}
                onChange={update("email")}
              />
              {errors.email && (
                <p data-testid="error-email" className="mt-2 text-xs text-red-400/90">
                  {errors.email}
                </p>
              )}
            </div>
            <input
              data-testid="contact-phone-input"
              className={inputCls}
              placeholder="Phone"
              value={form.phone}
              onChange={update("phone")}
            />
            <input
              data-testid="contact-location-input"
              className={inputCls}
              placeholder="Location"
              value={form.location}
              onChange={update("location")}
            />
            <input
              data-testid="contact-date-input"
              className={`${inputCls} [color-scheme:dark]`}
              type="date"
              value={form.preferred_date}
              onChange={update("preferred_date")}
              aria-label="Preferred Campaign Date"
            />
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/30">
              Service Required *
            </label>
            <select
              data-testid="contact-service-select"
              value={form.service}
              onChange={update("service")}
              className={`${inputCls} appearance-none cursor-pointer [&>option]:bg-[#101012] [&>option]:text-white${errors.service ? " border-red-400/70 focus:border-red-400" : ""}`}
            >
              <option value="">Select a service</option>
              {SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            {errors.service && (
              <p data-testid="error-service" className="mt-2 text-xs text-red-400/90">
                {errors.service}
              </p>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-[0.3em] text-white/30">
              Message *
            </label>
            <textarea
              data-testid="contact-message-input"
              className={`${inputCls} resize-none mt-2${errors.message ? " border-red-400/70 focus:border-red-400" : ""}`}
              rows={4}
              placeholder="Tell us about your project, idea, event, installation or anything else you're planning."
              value={form.message}
              onChange={update("message")}
            />
            {errors.message && (
              <p data-testid="error-message" className="mt-2 text-xs text-red-400/90">
                {errors.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            data-testid="contact-submit-button"
            disabled={loading}
            className="group mt-2 inline-flex items-center gap-3 self-start border border-white bg-white text-black px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-transparent hover:text-white transition-colors duration-300 disabled:opacity-50"
          >
            {loading ? "Sending…" : t("contact_submit", "Start Your Project")}
            <ArrowUpRight
              size={18}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
            />
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
